// src/components/PlanLimitsBanner.jsx
// =============================================================================
// Displays a banner when user has upgraded from trial but exceeds plan limits
// Shows a dropdown with current vs allowed counts, links to Settings to resolve
// =============================================================================

import React, { useState, useRef, useEffect } from 'react';

// =============================================================================
// PLAN LIMITS CONFIGURATION
// Must match Firestore security rules limits
// =============================================================================
const PLAN_LIMITS = {
  basic: {
    pitches: 3,
    teams: 60,
    admins: 1,
    users: 4
  },
  standard: {
    pitches: 8,
    teams: 100,
    admins: 2,
    users: 8
  },
  premium: {
    pitches: 20,
    teams: 150,
    admins: 3,
    users: 12
  }
};

// =============================================================================
// PLAN LIMITS BANNER COMPONENT
// =============================================================================
const PlanLimitsBanner = ({ 
  subscriptionTier,
  subscriptionStatus,
  currentPitches,
  currentTeams,
  currentAdmins,
  currentUsers
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get limits for current tier
  const limits = PLAN_LIMITS[subscriptionTier] || PLAN_LIMITS.basic;

  // Check if any resource is over limit
  const isOverPitches = currentPitches > limits.pitches;
  const isOverTeams = currentTeams > limits.teams;
  const isOverAdmins = currentAdmins > limits.admins;
  const isOverUsers = currentUsers > limits.users;
  const isOverAnyLimit = isOverPitches || isOverTeams || isOverAdmins || isOverUsers;

  // Only show banner if:
  // 1. User has an ACTIVE paid subscription (not trial, not expired, not free)
  // 2. User is over at least one limit
  // Don't show for expired/free users - they're already restricted by the trial expired banner
  const shouldShowBanner = subscriptionStatus === 'active' && subscriptionTier !== 'free' && isOverAnyLimit;

  if (!shouldShowBanner) {
    return null;
  }

  // Helper to render a limit row with colour coding
  const renderLimitRow = (label, current, allowed, isOver) => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: '1px solid #e5e7eb'
    }}>
      <span style={{ 
        color: '#374151',
        fontSize: '14px'
      }}>
        {label}
      </span>
      <span style={{
        fontWeight: '600',
        fontSize: '14px',
        color: isOver ? '#dc2626' : '#16a34a'
      }}>
        {current} / {allowed === 999 ? '∞' : allowed}
        {isOver && (
          <span style={{ marginLeft: '8px', fontSize: '12px' }}>⚠️</span>
        )}
      </span>
    </div>
  );

  return (
    <div style={{
      position: 'sticky',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: '#fef3c7', // Amber/warning background
      color: '#92400e',
      padding: '16px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
      flexWrap: 'wrap',
      gap: '12px',
      borderBottom: '2px solid #f59e0b'
    }}>
      {/* Message */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1,
        minWidth: '250px'
      }}>
        {/* Warning Icon */}
        <div style={{
          fontSize: '24px',
          flexShrink: 0
        }}>
          ⚠️
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#92400e'
          }}>
            Some features exceed your plan limits
          </div>
          <div style={{
            fontSize: '14px',
            color: '#a16207',
            marginTop: '2px'
          }}>
            New allocations are restricted until resolved
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '12px',
        flexShrink: 0,
        position: 'relative'
      }} ref={dropdownRef}>
        {/* View Details Dropdown Button */}
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          style={{
            padding: '10px 20px',
            backgroundColor: 'white',
            color: '#92400e',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        >
          View Details
          <span style={{
            transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            display: 'inline-block'
          }}>
            ▼
          </span>
        </button>

        {/* Dropdown Panel */}
        {showDropdown && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e5e7eb',
            padding: '16px',
            minWidth: '250px',
            zIndex: 1001
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#6b7280',
              textTransform: 'uppercase',
              marginBottom: '12px',
              letterSpacing: '0.05em'
            }}>
              {subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)} Plan Limits
            </div>
            
            {renderLimitRow('Pitches', currentPitches, limits.pitches, isOverPitches)}
            {renderLimitRow('Teams', currentTeams, limits.teams, isOverTeams)}
            {renderLimitRow('Admin Users', currentAdmins, limits.admins, isOverAdmins)}
            {renderLimitRow('Users', currentUsers, limits.users, isOverUsers)}
            
            <div style={{
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: '1px solid #e5e7eb',
              fontSize: '12px',
              color: '#6b7280'
            }}>
              Archive or remove items to continue creating allocations
            </div>
          </div>
        )}
        
        {/* Go to Settings Button */}
        <button
          onClick={() => window.location.href = '/settings'}
          style={{
            padding: '10px 20px',
            backgroundColor: '#243665',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            transition: 'transform 0.2s, box-shadow 0.2s',
            boxShadow: '0 2px 8px rgba(36, 54, 101, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.boxShadow = '0 4px 12px rgba(36, 54, 101, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 2px 8px rgba(36, 54, 101, 0.3)';
          }}
        >
          Go to Settings
        </button>
      </div>
    </div>
  );
};

// Export the limits config so other components can use it
export { PLAN_LIMITS };
export default PlanLimitsBanner;
